import React, { createContext, useCallback, useContext, useRef, useState, useEffect } from 'react';

export type ToastVariant = 'success' | 'error' | 'info' | 'warning';

export interface Toast {
    id: string;
    title?: string;
    message: string;
    variant: ToastVariant;
    createdAt: number;
    ttl?: number; // ms lifespan (auto dismiss)
    hideIcon?: boolean; // Hide the left icon
}

interface ToastContextValue {
    toasts: Toast[];
    push: (toast: Omit<Toast, 'id' | 'createdAt'> & { id?: string }) => string;
    dismiss: (id: string) => void;
    clear: () => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export const DEFAULT_TOAST_TTL = 5000; // 5s
const MAX_TOASTS = 5; // avoid overflow

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [toasts, setToasts] = useState<Toast[]>([]);
    const timers = useRef<Record<string, any>>({});

    const dismiss = useCallback((id: string) => {
        setToasts(prev => prev.filter(t => t.id !== id));
        if (timers.current[id]) {
            clearTimeout(timers.current[id]);
            delete timers.current[id];
        }
    }, []);

    const schedule = useCallback((toast: Toast) => {
        const ttl = toast.ttl ?? DEFAULT_TOAST_TTL;
        if (ttl > 0) {
            timers.current[toast.id] = setTimeout(() => dismiss(toast.id), ttl);
        }
    }, [dismiss]);

    const push: ToastContextValue['push'] = useCallback((toastInput) => {
        const id = toastInput.id || crypto.randomUUID();
        setToasts(prev => {
            const next: Toast[] = [...prev, { ...toastInput, id, createdAt: Date.now() }];
            // enforce max (drop oldest)
            if (next.length > MAX_TOASTS) {
                next.splice(0, next.length - MAX_TOASTS);
            }
            return next;
        });
        schedule({ ...toastInput, id, createdAt: Date.now() });
        return id;
    }, [schedule]);

    const clear = useCallback(() => {
        Object.values(timers.current).forEach(t => clearTimeout(t));
        timers.current = {};
        setToasts([]);
    }, []);

    useEffect(() => () => { // cleanup on unmount
        Object.values(timers.current).forEach(t => clearTimeout(t));
    }, []);

    return (
        <ToastContext.Provider value={{ toasts, push, dismiss, clear }}>
            {children}
        </ToastContext.Provider>
    );
};

export const useToast = () => {
    const ctx = useContext(ToastContext);
    if (!ctx) throw new Error('useToast must be used within ToastProvider');
    return ctx;
};
